import React, { useEffect, useState, useRef, useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'

// Components
import Icon from './icon/Icon'
import Button, { IProps as IButtonProps } from './Button'
import Link from './Link'
import { GridRow, GridCol } from './grid'
import Text from './Text'
import Avatar, { AvatarSize } from './Avatar'
import StepBar from './StepBar'
import { UserTags } from './card/UserDetails'
import { Row, RowAligned } from './layout/LayoutAtoms'
import GoalCount from './GoalCount'
import { ContextMenu, ISection } from './ContextButton'

// Styles
import { mqFrom, mqTo } from '../styles/mediaQueries'
import { shadows } from '../styles/effects'
import { applyTextStyle } from '../styles/typography'

// Types
import { IconType } from './icon/IconType'
import { ICompanyMember } from '../common/models/Company'

export interface IPath {
  name: string
  // In case the page doesn't have a landing page yet: should be visible in menu structure but not clickable
  route?: string
}

export interface INavTabs {
  path: string
  name: string
  title: string
  onClick?: () => void
  disabled?: boolean
  active?: boolean
  // A small red dot next to the label — no count, just a look-here cue.
  badge?: boolean
}

interface INavButton {
  icon?: IconType
  // Optional: a button that only opens a context menu drives its own toggle.
  onClick?: () => void
  caption?: string
  disabled?: boolean
  colorVariant?: IButtonProps['colorVariant']
  // The icon after the caption, for a button that moves on, like Send
  switchOrder?: boolean
  // Cypress handle, forwarded to the button.
  'data-cy'?: string
}

// The fixed band a page can hang between the top bar and its header
export const BANNER_HEIGHT = 56

export interface ISecondaryNavProps {
  version: 'mainPage' | 'subPage' | 'form'
  // Left out for a header that is only tabs
  title?: string
  // An icon beside the title, for what the page is about, such as its settings
  titleButton?: {
    icon: IconType
    ariaLabel: string
    onClick: () => void
    disabled?: boolean
  }
  subtitle?: string
  label?: string | React.ReactNode
  breadcrumb?: IPath[]
  button?: INavButton
  secondButton?: INavButton & {
    iconSize?: number
    // When set, the button opens this context menu instead of firing onClick.
    menu?: ISection[]
  }
  // A ••• button after the buttons, for the page's less used actions
  menu?: ISection[]
  user?: ICompanyMember
  formValues?: {
    total: number
    done: number
  }
  className?: string
  // A stepped form: the step names under the title, and the one the user is on
  steps?: { labels: string[]; current: number }
  navigateStepBack?: () => void
  avatar?: string
  icon?: string
  backgroundImg?: string
  tabs?: INavTabs[]
  // A fixed band above the header, such as whose profile you are on. The tabs pin under it.
  banner?: React.ReactNode
}

let originalOffset: number

// A tab's `name` is an internal identifier, never translated (that is `title`),
// so it doubles as the stable handle e2e specs select on.
const tabTestId = (name: string) =>
  `nav-tab-${name.toLowerCase().replace(/\s+/g, '-')}`

const SecondaryNavigation: React.FC<ISecondaryNavProps> = ({
  title,
  titleButton,
  subtitle,
  version,
  tabs,
  banner,
  user,
  avatar,
  backgroundImg,
  icon,
  button,
  secondButton,
  menu,
  label,
  breadcrumb,
  formValues,
  navigateStepBack,
  className,
  steps,
}) => {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const history = useHistory()
  const big = version === 'mainPage'
  // A tabbed subPage scrolls its header away while the tabs pin — same model as mainPage.
  // Tab-less subPages and forms keep their fixed nav.
  const scrollAway = version === 'subPage' && !!tabs

  // Where the tabs pin: under the top bar, and under the banner when there is one
  const pinTop = 56 + (banner ? BANNER_HEIGHT : 0)

  const fixedDiv = useRef<HTMLDivElement>(null)
  const [tabsFixed, setTabsFixed] = useState<boolean>(false)
  const [tabsHeight, setTabsHeight] = useState<number>(0)

  // Open-state for the second button's context menu (when one is provided).
  // Closes on any outside click, matching the app's kebab-menu pattern.
  const [secondMenuOpen, setSecondMenuOpen] = useState<boolean>(false)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  useEffect(() => {
    const close = () => setSecondMenuOpen(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [])

  const handleScroll = () => {
    if (!fixedDiv.current) return
    setTabsFixed(window.scrollY >= originalOffset)
  }

  useEffect(() => {
    if (
      !window ||
      (version !== 'mainPage' && !scrollAway) ||
      !tabs ||
      !fixedDiv ||
      !fixedDiv.current
    ) {
      return
    }
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [tabs, version, fixedDiv, scrollAway])

  useEffect(() => {
    if (!fixedDiv || !fixedDiv.current || !tabs) return
    if (!tabsFixed) {
      // The scroll at which the tabs reach the bar they pin under, measured rather
      // than assumed, so a header with or without space above its tabs pins on time
      originalOffset =
        fixedDiv.current.getBoundingClientRect().top + window.scrollY - pinTop
      // Capture the tab-bar height while in flow so the spacer can reserve it once pinned
      setTabsHeight(fixedDiv.current.offsetHeight)
    }
    // tabs?.length is in the deps because tabs can render async (e.g. after data loads):
    // without it originalOffset stays undefined and the tabs never pin.
  }, [fixedDiv, user, avatar, icon, tabsFixed, tabs?.length, pinTop])

  // Back on the left and the title centred, for a stepped form or a page with a way back
  const stepped = !!steps || !!navigateStepBack

  // Nothing to show above the tabs: no title, no person and no actions
  const hasHead = !!(title || user || avatar || icon || button || secondButton)

  return (
    <>
      {banner && (
        <>
          <Banner>{banner}</Banner>
          <BannerSpacer />
        </>
      )}
      <Container
        className={className}
        id="secondaryNav"
        bigVersion={big}
        formVariant={version === 'form'}
        $scrollAway={scrollAway}
        $flush={!!tabs && !hasHead}
      >
        <InnerWrapper sub={version === 'subPage' && !tabs}>
          <StyledGridRow alignType="center">
            {/* A stepped form header lines up with the 8-column form beneath it */}
            <StyledGridCol xs={12} l={steps ? 8 : 12}>
              {breadcrumb && (
                <Paths backgroundImg={backgroundImg}>
                  {breadcrumb.map((path, index) => (
                    <React.Fragment key={`breadcrumb-${index}`}>
                      {index !== 0 && breadcrumb.length > 1 && (
                        <StyledIcon
                          size={12}
                          color={
                            backgroundImg
                              ? theme.colors.lightest
                              : theme.colors.textLighter
                          }
                          type="ChevronRight"
                        />
                      )}
                      <Link
                        disableUnderline
                        color={backgroundImg ? 'lightest' : 'text72'}
                        hoverColor="primary"
                        caption={t(path.name)}
                        icon={
                          path.route && breadcrumb.length === 1
                            ? 'ChevronLeft'
                            : undefined
                        }
                        onClick={
                          path.route
                            ? () => history.push(path.route || '')
                            : undefined
                        }
                      />
                    </React.Fragment>
                  ))}
                </Paths>
              )}
              {backgroundImg && big && (
                <BackgroundImg backgroundImg={backgroundImg} />
              )}
              {version === 'form' ? (
                <>
                  <FormMain>
                    <div>
                      {subtitle && (
                        <Text
                          color="textLight"
                          textStyle="labelSmallCaps"
                          textAlign="left"
                        >
                          {subtitle}
                        </Text>
                      )}
                      {!stepped && (
                        <Text
                          textStyle="displayS"
                          textAlign="left"
                          marginBottom={formValues ? 12 : 0}
                        >
                          {title}
                        </Text>
                      )}
                      {formValues && (
                        <GoalCount
                          actions={[
                            {
                              total: formValues?.total,
                              complete: formValues?.done,
                            },
                          ]}
                        />
                      )}
                    </div>

                    {navigateStepBack && (
                      <FormButton
                        icon="ChevronLeft"
                        caption={t('general.actions.back')}
                        hide={steps?.current === 0}
                        color="lightest"
                        textColor="primary"
                        hoverColor="primary"
                        hoverTextColor="lightest"
                        onClick={navigateStepBack}
                      />
                    )}
                    <Center>
                      {stepped && (
                        <Text textStyle="displayS" textAlign="center">
                          {title}
                        </Text>
                      )}
                    </Center>
                    <ButtonContainer $pinned>
                      {button && (
                        <FormButton
                          icon={button.icon}
                          caption={button.caption}
                          disabled={button.disabled}
                          colorVariant="secondary"
                          onClick={button.onClick}
                          data-cy={button['data-cy']}
                        />
                      )}
                      {label && (
                        <Text
                          tag="div"
                          color="textLight"
                          marginTop={12}
                          textStyle="caption"
                        >
                          {label}
                        </Text>
                      )}
                    </ButtonContainer>
                  </FormMain>
                  {steps && (
                    <StyledStepBar
                      steps={steps.labels}
                      current={steps.current}
                      surface="textLightest"
                    />
                  )}
                </>
              ) : (
                hasHead && (
                  <Main
                    bigVersion={big}
                    backgroundImg={!!backgroundImg && big}
                    lessMargin={!breadcrumb}
                  >
                    {(user || avatar || icon) && (
                      <StyledAvatar
                        user={user}
                        imgSrc={avatar}
                        initials={icon}
                        color={
                          avatar
                            ? 'lightest'
                            : icon
                            ? 'primaryLight'
                            : 'primary'
                        }
                        size={AvatarSize.XLarge}
                      />
                    )}
                    <TitleBlock>
                      <TitleRow>
                        <StyledText
                          big={big}
                          textStyle={big ? 'displayL' : 'displayS'}
                          color={backgroundImg ? 'lightest' : 'text'}
                        >
                          {title}
                        </StyledText>
                        {titleButton && (
                          <Button
                            colorVariant="quaternary"
                            icon={titleButton.icon}
                            iconSize={16}
                            ariaLabel={titleButton.ariaLabel}
                            onClick={titleButton.onClick}
                            disabled={titleButton.disabled}
                          />
                        )}
                      </TitleRow>
                      {user && <StyledUserTags user={user} whiteBackground />}
                    </TitleBlock>
                    {(button || secondButton) && (
                      <ButtonContainer $pinned={big}>
                        <RowAligned>
                          {button && (
                            <StyledButton
                              big={big}
                              icon={button.icon}
                              onClick={button.onClick}
                              disabled={button.disabled}
                              caption={button.caption}
                              colorVariant={button.colorVariant}
                              switchOrder={button.switchOrder}
                              color="lightest"
                              textColor="primary"
                              hoverColor="primary"
                              hoverTextColor="lightest"
                            />
                          )}
                          {secondButton &&
                            (secondButton.menu ? (
                              <SecondMenuWrapper>
                                <StyledButton
                                  big={big}
                                  iconSize={secondButton.iconSize}
                                  icon={secondButton.icon}
                                  data-cy={secondButton['data-cy']}
                                  onClick={(e) => {
                                    e?.stopPropagation()
                                    setSecondMenuOpen((open) => !open)
                                  }}
                                  disabled={secondButton.disabled}
                                  caption={secondButton.caption}
                                  colorVariant={secondButton.colorVariant}
                                  switchOrder={secondButton.switchOrder}
                                />
                                {secondMenuOpen && (
                                  <NavContextMenu
                                    sections={secondButton.menu}
                                    closeMenu={() => setSecondMenuOpen(false)}
                                  />
                                )}
                              </SecondMenuWrapper>
                            ) : (
                              <StyledButton
                                big={big}
                                iconSize={secondButton.iconSize}
                                icon={secondButton.icon}
                                data-cy={secondButton['data-cy']}
                                onClick={secondButton.onClick}
                                disabled={secondButton.disabled}
                                caption={secondButton.caption}
                                colorVariant={secondButton.colorVariant}
                                switchOrder={secondButton.switchOrder}
                              />
                            ))}
                          {menu && (
                            <SecondMenuWrapper>
                              <StyledButton
                                big={big}
                                caption="•••"
                                ariaLabel={t('general.options')}
                                colorVariant="secondary"
                                onClick={(e) => {
                                  e?.stopPropagation()
                                  setMenuOpen((open) => !open)
                                }}
                              />
                              {menuOpen && (
                                <NavContextMenu
                                  sections={menu}
                                  closeMenu={() => setMenuOpen(false)}
                                />
                              )}
                            </SecondMenuWrapper>
                          )}
                        </RowAligned>
                        {label && (
                          <Text
                            tag="div"
                            color="textLight"
                            textStyle="caption"
                            marginTop={16}
                          >
                            {label}
                          </Text>
                        )}
                      </ButtonContainer>
                    )}
                  </Main>
                )
              )}
              {subtitle && version !== 'form' && (
                <Text color="textLight" textStyle="bodySmall">
                  {subtitle}
                </Text>
              )}
              {tabs && (
                <>
                  <Sticky
                    id="secondaryNavTabs"
                    ref={fixedDiv}
                    fixed={tabsFixed}
                    $top={pinTop}
                  >
                    <InnerWrapperTabs>
                      {tabs.map((tab, index) => {
                        const active =
                          tab.active ||
                          history.location.pathname.includes(tab.path)
                        return (
                          <Tab
                            key={`nav-tab-${index}`}
                            data-cy={tabTestId(tab.name)}
                            disabled={tab.disabled}
                            active={active}
                            onClick={
                              tab.disabled
                                ? undefined
                                : () => {
                                    if (tab.onClick) {
                                      tab.onClick()
                                    } else {
                                      history.push(tab.path)
                                    }
                                  }
                            }
                          >
                            {t(tab.title)}
                            {tab.badge && <NewDot />}
                          </Tab>
                        )
                      })}
                    </InnerWrapperTabs>
                  </Sticky>
                  {scrollAway && (
                    // Reserve the tabs' height once they pin, so the content doesn't jump up
                    <TabsSpacer height={tabsFixed ? tabsHeight : 0} />
                  )}
                </>
              )}
            </StyledGridCol>
          </StyledGridRow>
        </InnerWrapper>
      </Container>
    </>
  )
}

export default SecondaryNavigation

const StyledStepBar = styled(StepBar)`
  margin-bottom: 16px;

  ${mqFrom.M`
    margin-bottom: 24px;
  `}
`

// Flush with the screen edges, right under the top bar
const Banner = styled.div`
  position: fixed;
  top: 56px;
  left: 0;
  width: 100vw;
  height: ${BANNER_HEIGHT}px;
  z-index: 99;
`

const BannerSpacer = styled.div`
  height: ${BANNER_HEIGHT}px;
`

const Container = styled.div<{
  bigVersion: boolean
  formVariant: boolean
  $scrollAway?: boolean
  // Only tabs: they start at the top edge, on every screen
  $flush?: boolean
}>`
  /* Only tabs: they carry their own colour, so the place they keep while pinned
     never shows as a grey band, such as when a trackpad bounces the page */
  background-color: ${({ $flush, theme }) =>
    $flush ? 'transparent' : theme.colors.textLightest};
  padding-top: ${({ $flush }) => ($flush ? 0 : 24)}px;
  position: relative;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding-left: 16px;
  padding-right: 16px;

  ${mqFrom.M`
    padding-left: 48px;
    padding-right: 48px;
  `}

  ${mqFrom.L`
    padding-left: 64px;
    padding-right: 64px;
  `}

  ${({ bigVersion, formVariant, $scrollAway, $flush, theme }) =>
    bigVersion || $scrollAway
      ? // In the flow so the header scrolls away; only the tabs pin (see Sticky).
        // mainPage pre-reserves 424px for its big header; scrollAway uses a TabsSpacer instead.
        css`
          ${bigVersion
            ? mqFrom.M`height: 424px;`
            : !$flush && mqTo.M`padding-top: 16px;`}
        `
      : css`
          position: fixed;
          top: ${formVariant ? 0 : 56}px;
          left: 0;
          width: 100vw;
          z-index: 99;
          border-bottom: ${formVariant
            ? `2px solid ${theme.colors.textLighter}`
            : 'none'};

          ${mqTo.M`
            padding-top: 16px;
          `};
        `}
`

const InnerWrapper = styled.div<{ sub: boolean }>`
  margin: 0 auto;
  width: 100%;
  height: 100%;
  position: relative;
  ${({ sub, theme }) =>
    sub &&
    css`
      padding-bottom: 24px;
      border-bottom: 2px solid ${theme.colors.textLighter};
    `}

  ${mqFrom.L`
    max-width: 1184px;
  `}
`

const StyledGridRow = styled(GridRow)`
  height: 100%;
`

const StyledGridCol = styled(GridCol)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const StyledAvatar = styled(Avatar)`
  border: 4px solid ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthThree};
  margin-bottom: 24px;
  flex-shrink: 0;

  ${mqFrom.M`
    margin: 0 48px 0 0;
  `}
`

const StyledUserTags = styled(UserTags)`
  margin-top: 12px;
  justify-content: center;

  ${mqFrom.M`
    justify-content: flex-start;
  `}

  ${mqFrom.L`
    flex-wrap: nowrap;
  `}
`

const FormMain = styled(Row)`
  margin-bottom: 16px;

  ${mqFrom.M`
    margin-bottom: 24px;
  `}
`

const Center = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4px;

  ${mqFrom.M`
    align-items: center;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  `}
`

const ButtonContainer = styled.div<{ $pinned?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;
  flex-shrink: 0;

  /* mainPage and form headers stack their content on a phone, so their actions
     are pinned to the top-right corner. A subPage header is a row: there the
     actions stay in flow beside the title, clear of the absolute breadcrumb. */
  ${({ $pinned }) =>
    $pinned &&
    css`
      ${mqTo.M`
        position: absolute;
        right: 0px;
        top: 0px;
      `}
    `}
`

const FormButton = styled(Button)<{ hide?: boolean }>`
  width: fit-content;

  ${({ hide }) =>
    hide &&
    `
    opacity: 0;
  `}

  ${mqTo.M`
    > span {
      margin-right: 0;
    }

     > label {
      ${({ icon, caption }) =>
        icon && caption ? 'display: none;' : 'display: inline-block;'}
    }
  `}
`

const BackgroundImg = styled.div<{ backgroundImg: string }>`
  ${({ backgroundImg }) =>
    backgroundImg &&
    `
      width: 100%;
      border-radius: 24px;
      position: absolute;
      top: 0;
      bottom: 174px;
      left: 0;
      background-image: url('${backgroundImg}');
      background-position: center;
      background-size: cover;
    `}

  ${mqFrom.M`
      bottom: 88px;
    `}
`

const Main = styled.div<{
  bigVersion: boolean
  lessMargin?: boolean
  backgroundImg?: boolean
}>`
  display: flex;
  align-items: center;
  flex-direction: ${({ bigVersion }) => (bigVersion ? 'column' : 'row')};
  /* As tall as its button, so a header is the same height with or without one and the
     tabs under it don't shift from tab to tab */
  ${({ bigVersion }) => !bigVersion && 'min-height: 32px;'}
  margin: ${({ bigVersion, lessMargin, backgroundImg }) =>
    backgroundImg
      ? '180px 0 16px'
      : bigVersion
      ? '64px 0 48px'
      : lessMargin
      ? '8px 0'
      : '24px 0 8px'};

  ${mqFrom.M`
    flex-direction: row;
    margin: ${({
      bigVersion,
      lessMargin,
      backgroundImg,
    }: {
      bigVersion: boolean
      lessMargin?: boolean
      backgroundImg?: boolean
    }) =>
      backgroundImg
        ? '184px 32px 0px 48px'
        : bigVersion
        ? '96px 0 88px'
        : lessMargin
        ? '8px 0'
        : '28px 0 8px'};
  `}
`

const StyledText = styled(Text)<{ big: boolean }>`
  ${({ big }) =>
    big &&
    css`
      ${mqTo.M`
        text-align: center;
      `}
    `}

  ${mqTo.M`
    color: ${({ theme }) => theme.colors.text};
  `}
`

const StyledButton = styled(Button)<{ big: boolean }>`
  z-index: 1;
  ${({ big }) =>
    big
      ? css`
          margin-top: 8px;
          ${mqFrom.M`
      margin: 0 0 0 auto;
    `}
        `
      : css`
          margin-left: auto;

          &:nth-of-type(2) {
            margin-left: 8px;
          }
        `}

  /* A phone only has room for the icon. The caption is clipped rather than
     removed, so the button keeps its accessible name; the icon's 8px gap is
     reset because the label still counts as the last child. */
  ${mqTo.S`
    ${({ icon, caption }: IButtonProps) =>
      icon && caption
        ? `
          > span {
            margin-right: 0;
          }

          > label {
            position: absolute;
            width: 1px;
            height: 1px;
            margin: -1px;
            padding: 0;
            overflow: hidden;
            clip-path: inset(50%);
            white-space: nowrap;
          }
        `
        : ''}
  `}
`

// The nav's ••• menu hangs off a button-sized anchor, where ContextMenu's
// mobile "width: 100%" would clip its own rows. Size it to its content instead,
// capped to the viewport so it can never run off the right edge. It also sits
// above the tabs bar (z-index 99), which would otherwise cover its top rows.
const NavContextMenu = styled(ContextMenu)`
  width: max-content;
  max-width: calc(100vw - 32px);
  z-index: 100;
`

const TitleBlock = styled.div`
  z-index: 1;
  min-width: 0;
`

// The title with its button beside it; without one, the title alone as before
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  > :only-child {
    flex: 1;
  }
`

// Relative anchor for a second button that drops a ContextMenu. Carries the 8px
// gap itself, since the wrapped button no longer matches StyledButton's
// :nth-of-type(2) spacing rule above.
const SecondMenuWrapper = styled.div`
  position: relative;
  display: flex;
  margin-left: 8px;
`

const Paths = styled.div<{ backgroundImg?: string }>`
  display: flex;
  align-items: center;
  position: absolute;
  z-index: 1;

  ${({ backgroundImg }) =>
    backgroundImg &&
    css`
      top: 24px;
      left: 24px;
    `}
`

const StyledIcon = styled(Icon)`
  margin: 0 8px;
`

const Sticky = styled.div<{ fixed: boolean; $top: number }>`
  margin-top: auto;
  top: ${({ $top }) => $top}px;
  position: ${({ fixed }) => (fixed ? 'fixed' : 'initial')};
  left: 0;
  background: ${({ theme }) => theme.colors.textLightest};
  z-index: 99;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  width: 100vw;
  padding-left: 16px;
  padding-right: 16px;
  overflow-x: scroll;
  display: flex;

  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }

  ${mqFrom.M`
    padding-left: 48px;
    padding-right: 48px;
  `}

  ${mqFrom.L`
    padding-left: 64px;
    padding-right: 64px;
  `}
`

const TabsSpacer = styled.div<{ height: number }>`
  height: ${({ height }) => height}px;
`

const InnerWrapperTabs = styled.ul`
  margin: 0;
  display: inline-flex;
  align-items: center;
  list-style: none;
  padding: 0px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.textLighter};

  ${mqFrom.M`
    width: 100%;
    margin: 0 auto;
  `}

  ${mqFrom.L`
    max-width: 1184px;
  `}
`

const Tab = styled.li<{ active: boolean; disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
  margin-right: 24px;
  padding: 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  ${applyTextStyle('subtitle')};
  letter-spacing: 0.5px;
  color: ${({ active, theme, disabled }) =>
    disabled
      ? theme.colors.textLighter
      : !active
      ? theme.colors.textLight
      : theme.colors.primary};
  border-bottom: 2px solid
    ${({ active, theme }) =>
      active ? theme.colors.primary : theme.colors.textLighter};
  margin-bottom: -2px;

  &:hover {
    color: ${({ theme, disabled }) =>
      disabled ? theme.colors.textLighter : theme.colors.primary};
  }

  ${mqFrom.M`
    margin-right: 32px;
    padding: 16px 0;
  `}
`

// A small red "look here" dot, also used next to names in PeopleRow.
export const NewDot = styled.div`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondary};
`
